import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import Price from "./Price";
import Name from "./Name";
import Description from "./Description";
import Category from "./Category";
import { get } from "lockr";
import {
  Currency,
  Language,
  MediaSource,
  NewProduct,
  ProductFormPrice,
  ProductFormText,
  ProductInterface,
  Segment,
  User,
} from "../../types/common.types";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { updateFeedback } from "../../redux/slices/AppState";
import { FEEDBACK_VARIANT } from "../extras/Alert";
import { productFormSubmit } from "./formUtils";
import { ProductType } from "../product";
import { useTranslation } from "../../hooks/useTranslation";
import Photo from "./Photo";
import { XHR_STATE } from "../../helpers/xhr";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";

export interface FormFields {
  names: ProductFormText;
  descriptions: ProductFormText;
  category: string;
  // price: string;
  prices: ProductFormPrice;
  inStock: boolean;
}
export interface FormStateProps {
  formFields: FormFields;
  setFields: React.Dispatch<React.SetStateAction<FormFields>>;
}

interface FormInterface {
  activeProduct: ProductInterface;
  productType: ProductType;
  showMediaPicker?: (value: boolean) => void;
}

const ModalProductUpdate = (props: FormInterface) => {
  const userData = get("user") as User | undefined;
  const productState = useSelector((state: RootState) => state.Product);
  const dispatch = useDispatch();
  const { xhr } = useSelector((state: RootState) => state.App);

  const { activeProduct, productType, showMediaPicker } = props;
  const isDrinkProduct = productType === ProductType.Drink;

  const submitTranslation = useTranslation("submit");

  let languages: Language[] = [];
  let currency: Currency = { name: "", prefix: "" };
  let segments: Segment[] = [];
  if (userData) {
    segments = userData.segments;
    languages = userData.languages;
    currency = userData.currency;
  }

  const [fields, setFields] = useState<FormFields>({
    names: {},
    descriptions: {},
    category: "",
    prices: {},
    inStock: true,
  });

  useEffect(() => {
    setFields({
      names: activeProduct.names,
      descriptions: activeProduct.descriptions,
      category: activeProduct.categoryId,
      prices: activeProduct.prices,
      inStock: activeProduct.inStock,
    });
    //eslint-disable-next-line
  }, []);

  const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { selectedFileInBase64, selectedMedia } = productState;

    if (!fields.category) {
      dispatch(
        updateFeedback({
          show: true,
          type: FEEDBACK_VARIANT.ERROR,
          message: `Please add a category`,
        })
      );
      return;
    }

    const shouldUpdate =
      JSON.stringify(fields) ===
      JSON.stringify({
        names: activeProduct.names,
        descriptions: activeProduct.descriptions,
        category: activeProduct.categoryId,
        prices: activeProduct.prices,
        inStock: activeProduct.inStock,
      });

    if (shouldUpdate) {
      return dispatch(
        updateFeedback({
          show: true,
          type: FEEDBACK_VARIANT.ERROR,
          message: "No field was changed",
        })
      );
    }

    let newPhoto = null;
    let newMediaSource = MediaSource.Upload;
    if (selectedFileInBase64 && typeof selectedFileInBase64 === "string") {
      newPhoto = selectedFileInBase64;
    } else if (selectedMedia.photo) {
      newPhoto = selectedMedia.id;
      newMediaSource = MediaSource.MediaPicker;
    }
    if (selectedFileInBase64) newPhoto = String(selectedFileInBase64);
    else if (selectedMedia.id) newPhoto = selectedMedia.id;

    const product: NewProduct = {
      descriptions: fields.descriptions,
      inStock: fields.inStock,
      names: fields.names,
      prices: fields.prices,
      categoryId: fields.category,
      photo: newPhoto,
      productPhoto: {
        ...activeProduct.productPhoto,
        mediaSource: activeProduct.mediaSource,
      },
      id: activeProduct.id,
      mediaSource: newPhoto ? newMediaSource : activeProduct.mediaSource,
    };

    await productFormSubmit(product, dispatch, "Update", isDrinkProduct);
  };

  return (
    <Form onSubmit={submitHandler}>
      <Stack spacing={1.5}>
        <Photo
          isMobile={false}
          activeProduct={activeProduct}
          showMediaPicker={showMediaPicker}
        />

        <Box>
          <SectionLabel>Product Details</SectionLabel>
          <Name
            languages={languages}
            formFields={fields}
            setFields={setFields}
          />
        </Box>

        <Box>
          <SectionLabel>Pricing</SectionLabel>
          <Price
            currency={currency}
            segments={segments}
            formFields={fields}
            setFields={setFields}
          />
        </Box>

        <Box>
          <SectionLabel>Category</SectionLabel>
          <Category
            formFields={fields}
            setFields={setFields}
            productType={productType}
          />
        </Box>

        <Box>
          <SectionLabel>Description</SectionLabel>
          <Description
            languages={languages}
            formFields={fields}
            setFields={setFields}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            pt: 1,
            borderTop: "1px solid rgba(131, 60, 65, 0.18)",
          }}
        >
          <Button
            variant="contained"
            sx={{
              textTransform: "none",
              minWidth: 160,
              borderRadius: "7px",
              bgcolor: "#833c41",
              boxShadow: "none",
              color: "#f9f3ef",
              fontWeight: 700,
              "&:hover": {
                bgcolor: "#9e4a50",
                boxShadow: "none",
              },
            }}
            type="submit"
          >
            {xhr === XHR_STATE.REQUESTING ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              submitTranslation
            )}
          </Button>
        </Box>
      </Stack>
    </Form>
  );
};

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    sx={{
      color: "#7a6560",
      fontFamily:
        '"DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
      fontSize: "10px",
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      mb: 0.75,
    }}
  >
    {children}
  </Typography>
);

export default ModalProductUpdate;
